import React, { useState, useRef, useEffect } from "react";

// Reusable full-screen drawing modal component
const DrawingModal = ({ 
  isOpen, 
  onClose, 
  selectedImage, 
  brushSize, 
  setBrushSize,
  onSave,
  title = "Draw Areas",
  description = "Paint over areas you want to modify. Hold Ctrl/Cmd to pan."
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
  const [lastDrawPoint, setLastDrawPoint] = useState(null);
  // Add cursor position state for circular cursor
  const [cursorPosition, setCursorPosition] = useState({ x: -100, y: -100 });
  const [showCursor, setShowCursor] = useState(false);

  useEffect(() => {
    if (isOpen && selectedImage && canvasRef.current) {
      initializeCanvas();
    }
  }, [isOpen, selectedImage]);

  const initializeCanvas = () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    
    const ctx = canvas.getContext('2d');
    
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      canvas.originalImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      
      setZoom(1);
      setPanOffset({ x: 0, y: 0 });
    };
    
    img.crossOrigin = "anonymous";
    img.src = selectedImage?.url || selectedImage;
  };

  const getEventPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  // Update cursor position
  const updateCursorPosition = (e) => {
    if (!canvasRef.current || !containerRef.current) return;
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    
    // Calculate cursor position relative to the container, offset by half brush size for centering
    const x = e.clientX - rect.left - (brushSize / 2);
    const y = e.clientY - rect.top - (brushSize / 2);
    
    setCursorPosition({ x, y });
  };

  const handleMouseEnter = () => {
    setShowCursor(true);
  };

  const handleMouseLeave = () => {
    setShowCursor(false);
    stopDrawing();
  };

  const startDrawing = (e) => {
    updateCursorPosition(e);
    
    if (e.ctrlKey || e.metaKey) {
      setIsPanning(true);
      setLastPanPoint({ x: e.clientX, y: e.clientY });
      return;
    }
    
    setIsDrawing(true);
    const pos = getEventPos(e);
    setLastDrawPoint(pos);
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = 'rgba(255, 0, 0, 0.8)';
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, brushSize / 2, 0, 2 * Math.PI);
    ctx.fill();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    setIsPanning(false);
    setLastDrawPoint(null);
  };

  const draw = (e) => {
    updateCursorPosition(e);
    
    if (isPanning) {
      const deltaX = e.clientX - lastPanPoint.x;
      const deltaY = e.clientY - lastPanPoint.y;
      setPanOffset(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      setLastPanPoint({ x: e.clientX, y: e.clientY });
      return;
    }

    if (!isDrawing || !lastDrawPoint) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const currentPos = getEventPos(e);
    
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = 'rgba(255, 0, 0, 0.8)';
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    ctx.beginPath();
    ctx.moveTo(lastDrawPoint.x, lastDrawPoint.y);
    ctx.lineTo(currentPos.x, currentPos.y);
    ctx.stroke();
    
    ctx.fillStyle = 'rgba(255, 0, 0, 0.8)';
    ctx.beginPath();
    ctx.arc(currentPos.x, currentPos.y, brushSize / 2, 0, 2 * Math.PI);
    ctx.fill();
    
    setLastDrawPoint(currentPos);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (canvas.originalImageData) {
      ctx.putImageData(canvas.originalImageData, 0, 0);
    }
  };

  const handleZoom = (delta) => {
    setZoom(prev => Math.min(Math.max(prev + delta, 0.1), 5));
  };

  const generateMask = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = canvas.width;
    maskCanvas.height = canvas.height;
    const maskCtx = maskCanvas.getContext('2d');
    
    maskCtx.fillStyle = 'black';
    maskCtx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);
    
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    const maskImageData = maskCtx.createImageData(canvas.width, canvas.height);
    const maskData = maskImageData.data;
    
    for (let i = 0; i < data.length; i += 4) {
      const red = data[i];
      const green = data[i + 1];
      const blue = data[i + 2];
      
      if (red > 150 && green < 100 && blue < 100 && red > green + 50 && red > blue + 50) {
        maskData[i] = 255;
        maskData[i + 1] = 255;
        maskData[i + 2] = 255;
        maskData[i + 3] = 255;
      } else {
        maskData[i] = 0;
        maskData[i + 1] = 0;
        maskData[i + 2] = 0;
        maskData[i + 3] = 255;
      }
    }
    
    maskCtx.putImageData(maskImageData, 0, 0);
    
    return new Promise((resolve) => {
      maskCanvas.toBlob(resolve, 'image/png');
    });
  };

  const handleSave = async () => {
    const maskBlob = await generateMask();
    const previewDataUrl = canvasRef.current?.toDataURL('image/png') || null;
    onSave(maskBlob, previewDataUrl);
    onClose();
  };

  const fitToScreen = () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    
    const containerRect = container.getBoundingClientRect();
    const canvasAspect = canvas.width / canvas.height;
    const containerAspect = containerRect.width / containerRect.height;
    
    if (canvasAspect > containerAspect) {
      const scale = (containerRect.width * 0.9) / canvas.width;
      setZoom(scale);
    } else {
      const scale = (containerRect.height * 0.9) / canvas.height;
      setZoom(scale);
    }
    
    setPanOffset({ x: 0, y: 0 });
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.95)',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        padding: '1rem 2rem',
        backgroundColor: '#1a1a1a',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #333'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{title}</h2>
          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', opacity: 0.8 }}>
            {description}
          </p>
        </div>
        
        {/* Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.9rem' }}>Brush:</span>
            <input
              type="range"
              min="2"
              max="100"
              value={brushSize}
              onChange={(e) => setBrushSize(parseInt(e.target.value))}
              style={{ 
                width: '100px',
                accentColor: '#4D7CFE'
              }}
            />
            <span style={{ fontSize: '0.9rem', minWidth: '40px' }}>{brushSize}px</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.9rem' }}>Zoom:</span>
            <button 
              onClick={() => handleZoom(-0.2)}
              style={{
                padding: '0.25rem 0.5rem',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              -
            </button>
            <span style={{ fontSize: '0.9rem', minWidth: '60px', textAlign: 'center' }}>
              {Math.round(zoom * 100)}%
            </span>
            <button 
              onClick={() => handleZoom(0.2)}
              style={{
                padding: '0.25rem 0.5rem',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              +
            </button>
            <button 
              onClick={fitToScreen}
              style={{
                padding: '0.25rem 0.5rem',
                backgroundColor: '#2ecc71',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.8rem'
              }}
            >
              Fit
            </button>
          </div>
          
          <button
            onClick={clearCanvas}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#e74c3c',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Clear
          </button>
          
          <button
            onClick={handleSave}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#27ae60',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Save Mask
          </button>
          
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#95a5a6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Canvas Container */}
      <div 
        ref={containerRef}
        style={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#2a2a2a',
          position: 'relative'
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            transform: `scale(${zoom}) translate(${panOffset.x}px, ${panOffset.y}px)`,
            cursor: isPanning ? 'grabbing' : 'none',
            border: '2px solid #4D7CFE',
            borderRadius: '4px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}
          onMouseDown={startDrawing}
          onMouseUp={stopDrawing}
          onMouseMove={draw}
          onMouseLeave={handleMouseLeave}
          onMouseEnter={handleMouseEnter}
          onWheel={(e) => {
            e.preventDefault();
            handleZoom(e.deltaY > 0 ? -0.1 : 0.1);
          }}
        />
                 {showCursor && !isPanning && (
           <div
             style={{
               position: 'absolute',
               left: `${cursorPosition.x}px`,
               top: `${cursorPosition.y}px`,
               width: `${brushSize}px`,
               height: `${brushSize}px`,
               borderRadius: '50%',
               border: '2px solid rgba(255, 0, 0, 0.8)',
               backgroundColor: 'rgba(255, 0, 0, 0.2)',
               pointerEvents: 'none',
               zIndex: 10001,
               transform: 'translate(0, 0)', // Ensure it's not affected by canvas transform
               boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.5)'
             }}
           />
         )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '0.75rem 2rem',
        backgroundColor: '#1a1a1a',
        color: 'white',
        fontSize: '0.85rem',
        borderTop: '1px solid #333'
      }}>
        💡 <strong>Tips:</strong> Hold Ctrl/Cmd and drag to pan • Scroll to zoom • Draw smooth strokes to mark areas
      </div>
    </div>
  );
};

export default DrawingModal; 